import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import DomainRuleExamples from './DomainRuleExamples';
import InfoButton from './InfoButton';
import './DomainRuleCreator.css';

interface DomainRuleCreatorProps {
  onSuccess: (config: any) => void;
  onCancel: () => void;
  existingDomains?: string[];
}

type ApproachType = 'generate' | 'convert';

// Common domain suggestions for autocomplete/reference
const COMMON_DOMAINS = [
  'ap', 'ar', 'gl', 'treasury', 'payroll', 'tax', 'revenue', 
  'inventory', 'fixed_assets', 'cost_accounting', 'financial_reporting',
  'consolidation', 'intercompany', 'operating_expenses', 'management_reporting',
  'lease_accounting', 'stock_compensation', 'debt_management', 'fx_hedging'
];

const DomainRuleCreator: React.FC<DomainRuleCreatorProps> = ({ onSuccess, onCancel, existingDomains = [] }) => {
  const [domainName, setDomainName] = useState<string>('');
  const [approach, setApproach] = useState<ApproachType>('generate');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showExamples, setShowExamples] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [editablePreview, setEditablePreview] = useState('');

  const getPlaceholderText = () => {
    if (!domainName.trim()) return 'Please enter a domain name first';
    
    const domainLabel = domainName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    switch (approach) {
      case 'generate':
        return `Example: Create comprehensive ${domainLabel} rules covering all standard policies, controls, and compliance requirements. Include specific thresholds for our company: approval limits, payment terms, aging buckets, etc.`;
      
      case 'convert':
        return `Paste your existing ${domainLabel} policies here. For example:

Invoice Approval Matrix:
- Under $1,000: Department manager
- $1,000-$10,000: Director approval
- $10,000-$50,000: VP approval  
- Over $50,000: CFO approval

Payment Terms:
- Standard vendors: Net 30
- Strategic partners: 2/10 Net 30
- New vendors: Prepayment or Net 15

Three-Way Match Requirements:
- All invoices over $500 require PO
- Tolerance: 5% or $100, whichever is less
- Services require approval from receiving department`;
      
      default:
        return '';
    }
  };

  const getApproachDescription = (approachType: ApproachType): string => {
    switch (approachType) {
      case 'generate':
        return 'AI creates comprehensive domain rules based on best practices';
      case 'convert':
        return 'Convert your existing written policies into structured JSON format';
      default:
        return '';
    }
  };

  const formatDomainName = (name: string): string => {
    // Convert to lowercase and replace spaces with underscores
    return name.toLowerCase().trim().replace(/\s+/g, '_');
  };

  const validateInput = (): boolean => {
    if (!domainName.trim()) {
      setError('Please enter a domain name');
      return false;
    }

    const formattedDomain = formatDomainName(domainName);
    
    // Check if domain already exists
    if (existingDomains.includes(formattedDomain)) {
      setError(`Domain "${formattedDomain}" already exists. Use the Edit feature to modify existing domain rules.`);
      return false;
    }
    
    // Validate domain name format
    if (!/^[a-z][a-z0-9_]*$/.test(formattedDomain)) {
      setError('Domain name must start with a letter and contain only lowercase letters, numbers, and underscores');
      return false;
    }
    
    if (!description.trim()) {
      setError('Please provide a description of the rules you want to create');
      return false;
    }
    
    if (description.trim().length < 20) {
      setError('Please provide more detail about the rules you need (at least 20 characters)');
      return false;
    }
    
    return true;
  };

  const handleGenerate = async () => {
    if (!validateInput()) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Format domain name
      const formattedDomain = formatDomainName(domainName);
      const domainLabel = domainName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Build the prompt based on approach
      let prompt = '';
      
      switch (approach) {
        case 'generate':
          prompt = `Create comprehensive domain rules for ${domainLabel}. ${description}
          
IMPORTANT: Focus on POLICIES and RULES, not procedures:
- Business rules and thresholds (approval limits, tolerances, deadlines)
- Compliance requirements and regulatory controls
- Validation rules and system constraints
- Documentation and audit requirements
- Segregation of duties and authorization matrix

Do NOT include step-by-step procedures (those belong in task frameworks).`;
          break;
          
        case 'convert':
          prompt = `Convert the following ${domainLabel} policies into structured domain rules JSON format:

${description}

IMPORTANT: Structure these policies into our standard format with:
- Core principles with flexibility notes
- Business rules with validation logic  
- Compliance requirements
- System controls and constraints
Preserve all specific thresholds, limits, and requirements from the source text.`;
          break;
      }
      
      // Call AI to generate the rules
      const response = await knowledgeBaseApi.createConfigWithAI(
        prompt,
        'domain',
        formattedDomain
      );
      
      if (response && response.success && response.config) {
        const configData = response.config.config_data || response.config;
        setPreview(configData);
        setEditablePreview(JSON.stringify(configData, null, 2));
      } else {
        throw new Error(response?.message || 'Invalid response from AI service');
      }
    } catch (error: any) {
      console.error('Failed to generate domain rules:', error);
      setError(error.message || 'Failed to generate domain rules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    
    try {
      // Parse the edited JSON to validate it
      const finalConfig = JSON.parse(editablePreview);
      
      // Validate required fields
      if (!finalConfig.name) {
        setError('Domain rules must have a name');
        return;
      }
      
      if (!finalConfig.core_principles && !finalConfig.business_rules) {
        setError('Domain rules must have either core principles or business rules');
        return;
      }
      
      // Format domain name for saving
      const formattedDomain = formatDomainName(domainName);
      
      // Save the configuration
      const saved = await knowledgeBaseApi.updateConfig(
        'domain',
        formattedDomain,
        finalConfig,
        undefined
      );
      
      onSuccess(saved);
    } catch (error: any) {
      if (error instanceof SyntaxError) {
        setError('Invalid JSON syntax. Please check your edits.');
      } else {
        setError(error.message || 'Failed to save domain rules');
      }
    }
  };

  const handleBack = () => {
    setPreview(null);
    setEditablePreview('');
    setError('');
  };

  if (preview) {
    // Show preview/edit screen
    return (
      <div className="domain-rule-creator">
        <h3>Review & Edit Domain Rules</h3>
        
        <div className="preview-header">
          <h4>{preview.name || `${domainName} Rules`}</h4>
          <p className="preview-domain">Domain: {domainName}</p>
        </div>
        
        <div className="preview-edit-section">
          <label>Edit the JSON below to refine the rules:</label>
          <textarea
            className="json-editor"
            value={editablePreview}
            onChange={(e) => setEditablePreview(e.target.value)}
            rows={20}
            spellCheck={false}
          />
          <small className="help-text">
            💡 Ensure all thresholds, limits, and compliance requirements are accurate for your organization.
          </small>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="button-group">
          <button onClick={handleSave} className="btn btn-primary">
            Save Domain Rules
          </button>
          <button onClick={handleBack} className="btn btn-secondary">
            Back to Edit
          </button>
          <button onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="domain-rule-creator">
      <div className="header-with-info">
        <h3>Create Domain Rules with AI</h3>
        <InfoButton
          title="What are Domain Rules?"
          content={{
            what: "Domain rules define the POLICIES and CONTROLS that govern a specific accounting area including business rules (approval limits, thresholds), compliance requirements, validations, and controls.",
            how: "Domain rules are applied when working within a specific accounting domain. They provide the guardrails and policies that must be followed, regardless of the specific task being performed.",
            precedence: "Domain rules have third priority after user instructions and task frameworks. They provide domain-specific guidance that supplements general safety checks."
          }}
        />
      </div>

      {/* Domain Name Input */}
      <div className="form-section">
        <label htmlFor="domain-name">
          New Domain Name
          <span className="required">*</span>
        </label>
        <div className="domain-input-wrapper">
          <input
            id="domain-name"
            type="text"
            value={domainName}
            onChange={(e) => {
              setDomainName(e.target.value);
              setError('');
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="e.g., procurement, audit_compliance, grant_management"
            disabled={loading}
          />
          <small className="input-hint">
            Domain name will be formatted as lowercase with underscores (e.g., "Grant Management" → "grant_management")
          </small>
          
          {/* Show suggestions dropdown */}
          {showSuggestions && domainName.length > 0 && (
            <div className="domain-suggestions">
              <div className="suggestions-header">Common domains (for reference):</div>
              {COMMON_DOMAINS
                .filter(d => d.includes(domainName.toLowerCase().replace(/\s+/g, '_')))
                .slice(0, 5)
                .map(domain => (
                  <div
                    key={domain}
                    className="suggestion-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setDomainName(domain.replace(/_/g, ' '));
                      setShowSuggestions(false);
                    }}
                  >
                    {domain.replace(/_/g, ' ')}
                  </div>
                ))}
            </div>
          )}
        </div>
        
        {/* Show formatted name preview */}
        {domainName && (
          <div className="domain-preview">
            Will be saved as: <code>{formatDomainName(domainName)}</code>
          </div>
        )}
      </div>

      {/* Approach Selection */}
      <div className="form-section">
        <label>Choose Approach</label>
        <div className="approach-options">
          <label className="approach-option">
            <input
              type="radio"
              value="generate"
              checked={approach === 'generate'}
              onChange={(e) => setApproach(e.target.value as ApproachType)}
              disabled={loading}
            />
            <div className="approach-content">
              <span className="approach-title">Generate comprehensive rules from description</span>
              <small>{getApproachDescription('generate')}</small>
            </div>
          </label>
          
          <label className="approach-option">
            <input
              type="radio"
              value="convert"
              checked={approach === 'convert'}
              onChange={(e) => setApproach(e.target.value as ApproachType)}
              disabled={loading}
            />
            <div className="approach-content">
              <span className="approach-title">Convert existing policies to JSON format</span>
              <small>{getApproachDescription('convert')}</small>
            </div>
          </label>
        </div>
        <small className="help-text">
          💡 To modify existing domain rules, close this dialog and use the "Edit with AI" feature on the existing domain.
        </small>
      </div>

      {/* Examples Section - Only show if domain matches a common domain */}
      {domainName && COMMON_DOMAINS.includes(formatDomainName(domainName)) && (
        <div className="form-section">
          <button
            type="button"
            className="toggle-examples-btn"
            onClick={() => setShowExamples(!showExamples)}
          >
            {showExamples ? '▼' : '▶'} View Example Rules for {domainName}
          </button>
          {showExamples && (
            <DomainRuleExamples
              domain={formatDomainName(domainName)}
              onUseExample={(example) => {
                setDescription(example);
                setShowExamples(false);
              }}
            />
          )}
        </div>
      )}

      {/* Description Input */}
      <div className="form-section">
        <label htmlFor="description">
          Describe your rules or requirements
          <span className="required">*</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={getPlaceholderText()}
          rows={10}
          disabled={loading}
        />
        <small className="help-text">
          {approach === 'generate' && 'Describe the policies, controls, and compliance requirements for this domain.'}
          {approach === 'convert' && 'Paste your existing policies, procedures, or documentation.'}
        </small>
      </div>

      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Action Buttons */}
      <div className="button-group">
        <button
          onClick={handleGenerate}
          disabled={loading || !domainName.trim() || !description.trim()}
          className="btn btn-primary"
        >
          {loading ? 'Generating...' : 'Generate Rules'}
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          className="btn btn-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DomainRuleCreator;